/**
 * CoConnect — DiseaseRiskCard
 *
 * Displays per-disease risk scores (0-10) with urgency badges and
 * recommendations. Uses the /api/weather/farm/:id/risk endpoint.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { apiClient } from '../services/api';
import { Spinner, Alert, StatusPill } from './ui';

interface Risk {
  disease: string;
  disease_label: string;
  risk_score: number;
  seasonal_baseline: number;
  weather_adjustment: number;
  confidence: number;
  primary_drivers: string[];
  recommendation: string;
  urgency: 'routine' | 'monitor' | 'prepare' | 'act_now' | 'critical';
}

interface RiskAssessment {
  farm_id: string | null;
  region: string | null;
  risks: Risk[];
  overall_risk_score: number;
  overall_urgency: 'routine' | 'monitor' | 'prepare' | 'act_now' | 'critical';
  assessed_at: string;
}

const urgencyTone: Record<string, 'success' | 'info' | 'warning' | 'danger'> = {
  routine: 'success',
  monitor: 'info',
  prepare: 'warning',
  act_now: 'danger',
  critical: 'danger',
};

const urgencyLabel: Record<string, string> = {
  routine: 'Routine',
  monitor: 'Monitor',
  prepare: 'Prepare',
  act_now: 'Act Now',
  critical: 'CRITICAL',
};

function scoreColor(score: number): string {
  if (score >= 9) return 'bg-danger-500';
  if (score >= 7) return 'bg-danger-400';
  if (score >= 5) return 'bg-warning-500';
  if (score >= 3) return 'bg-info-500';
  return 'bg-success-500';
}

interface Props {
  farmId?: string;
  lat?: number | null;
  lng?: number | null;
}

const DiseaseRiskCard: React.FC<Props> = ({ farmId, lat, lng }) => {
  const [risk, setRisk] = useState<RiskAssessment | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchRisk = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      let url = '/weather/farm/';
      if (farmId) {
        url = `/weather/farm/${farmId}/risk`;
      } else if (lat != null && lng != null) {
        url = `/weather/current?lat=${lat}&lng=${lng}`; // fallback: no risk endpoint w/o farm
        // We don't have a "by coords" risk endpoint — caller should pass farmId
        setError('Map a farm first to see disease risk.');
        setLoading(false);
        return;
      } else {
        setError('Map a farm first to see disease risk.');
        setLoading(false);
        return;
      }
      const res = await apiClient.get(url);
      setRisk(res.data);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load disease risk');
    } finally {
      setLoading(false);
    }
  }, [farmId, lat, lng]);

  useEffect(() => { fetchRisk(); }, [fetchRisk]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-6">
        <Spinner size="sm" /> <span className="ml-2 text-sm text-ink-500 dark:text-ink-400">Assessing disease risk…</span>
      </div>
    );
  }

  if (error) return <Alert tone="warning">{error}</Alert>;
  if (!risk) return null;

  return (
    <div className="farmer-disease-risk space-y-3">
      {/* Overall */}
      <div className="farmer-disease-overall flex items-center justify-between">
        <div>
          <p className="section-eyebrow">Overall disease risk</p>
          <p className="text-2xl font-semibold nums text-ink-900 dark:text-ink-50">
            {risk.overall_risk_score}<span className="text-sm text-ink-500 dark:text-ink-400">/10</span>
          </p>
        </div>
        <StatusPill tone={urgencyTone[risk.overall_urgency]} dot>
          {urgencyLabel[risk.overall_urgency]}
        </StatusPill>
      </div>

      {risk.region && (
        <p className="text-2xs text-ink-500 dark:text-ink-400">
          Region: {risk.region} · Assessed {new Date(risk.assessed_at).toLocaleString()}
        </p>
      )}

      {/* Per-disease risks */}
      <div className="farmer-disease-list space-y-2">
        {risk.risks.map(r => (
          <div key={r.disease} className="farmer-disease-item p-3 rounded-lg surface-inset">
            <div className="farmer-disease-head flex items-center justify-between mb-1.5">
              <p className="farmer-disease-name text-sm font-medium text-ink-900 dark:text-ink-50">{r.disease_label}</p>
              <div className="farmer-disease-score flex items-center gap-1.5">
                <span className="text-sm nums font-semibold text-ink-900 dark:text-ink-50">{r.risk_score}/10</span>
                <StatusPill tone={urgencyTone[r.urgency]} dot>{urgencyLabel[r.urgency]}</StatusPill>
              </div>
            </div>

            {/* Risk score bar */}
            <div className="h-2 rounded-full bg-ink-200 dark:bg-ink-700 overflow-hidden mb-2">
              <div
                className={`h-full ${scoreColor(r.risk_score)} transition-all duration-500`}
                style={{ width: `${(r.risk_score / 10) * 100}%` }}
              />
            </div>

            {/* Drivers */}
            {r.primary_drivers.length > 0 && (
              <p className="farmer-disease-drivers text-2xs text-ink-500 dark:text-ink-400 mb-1.5">
                <span className="font-medium">Drivers:</span> {r.primary_drivers.join(', ')}
              </p>
            )}

            {/* Recommendation */}
            <p className="farmer-disease-action text-xs text-ink-700 dark:text-ink-300">
              <span className="font-medium">Action:</span> {r.recommendation}
            </p>

            {/* Seasonal vs weather breakdown */}
            <div className="farmer-disease-meta mt-1.5 flex items-center gap-3 text-2xs text-ink-500 dark:text-ink-400">
              <span>Baseline: {r.seasonal_baseline}/10</span>
              <span>Weather adj: {r.weather_adjustment >= 0 ? '+' : ''}{r.weather_adjustment}</span>
              <span>Confidence: {Math.round(r.confidence * 100)}%</span>
            </div>
          </div>
        ))}
      </div>

      <div className="farmer-disease-source flex items-center justify-between text-2xs text-ink-400 dark:text-ink-500 border-t border-ink-200 dark:border-ink-800 pt-2">
        <span>Source: CC-DTIP Phase 5 + Phase 26 + live weather</span>
        <button onClick={fetchRisk} className="hover:text-ink-600 dark:hover:text-ink-300">↻ Refresh</button>
      </div>
    </div>
  );
};

export default DiseaseRiskCard;
